import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'

/**
 * Metro-map main-screen product analytics (catalog Gap G-1, the headline P0).
 * The most-used screen on every platform emitted ZERO behaviour events on web
 * — only a `metro_map.select.station_not_found` diagnostic. These typed
 * emitters close the gap with the byte-identical cross-platform names so F2
 * (route building), F3 (station exploration) and F5 (retention) get their
 * map-surface entry / heartbeat signals.
 *
 * Names + params are the cross-platform contract (iOS = canon); each emitter
 * forwards through the project {@link MetroAnalyticsReporter}, which routes to
 * GA4 + Amplitude (production-gated + fail-safe). OMIT-when-
 * null is applied by the sink, so optional params (`source`, `slot`, `target`)
 * are passed as `undefined` when absent and dropped on the wire.
 *
 * `station_tapped` is NOT here — it is emitted by the `MetroMapReducer`
 * `MapTapped` case (the reducer already resolves the tapped station id), via
 * the `ReportEvent` effect, so it stays in lockstep with the selection state
 * transition. This module owns the surface events with no reducer state
 * (`map_ready`, `my_location_tapped`, `search_entry_tapped`,
 * `deeplink_opened`).
 *
 * Intentionally-absent web events (documented, not gaps):
 *  - `line_tapped` — web has no independently-tappable line geometry; only
 *    station nodes carry tap-grids (see `interaction/tapGridHitTester`). No
 *    user interaction exists to attach it to, so it is not wired (n/a on web).
 *  - `theme_toggled` — web map theme follows the OS `prefers-color-scheme`
 *    media query (`MetroMapCanvas` `SystemThemeChanged`); there is no in-app
 *    light/dark toggle control. The only user-driven map theme change is the
 *    night transition, which is owned by `night_mode_*`
 *    (`analytics/nightModeEvents.ts`). Adding `theme_toggled{mode:night}`
 *    here would double-count it (catalog G-1 note). n/a on web.
 */

/** `map_ready.mode` — the effective map theme at first interactive frame. */
export type MapMode = 'light' | 'dark' | 'night'

/** `deeplink_opened.type` — the entry-point class. */
export type DeeplinkType = 'widget' | 'universal_link' | 'shortcut' | 'notification'

/**
 * `map_ready { duration_ms, cold_start, mode }` — first interactive frame of
 * the map after mount. Cold-start perf budget + the F5 session denominator
 * (separates "loaded" from crash/abandon). `duration_ms` is whole ms from app
 * mount to first frame; `cold_start` true on the first map mount of the
 * session, false on a later remount.
 */
export function reportMapReady(
  reporter: MetroAnalyticsReporter,
  params: { durationMs: number; coldStart: boolean; mode: MapMode },
): void {
  reporter('map_ready', {
    duration_ms: Math.max(0, Math.round(params.durationMs)),
    cold_start: params.coldStart,
    mode: params.mode,
  })
}

/**
 * `my_location_tapped` — user taps the locate-me / recenter control. No
 * params. Sizes locate-me usage / the value of the location permission.
 */
export function reportMyLocationTapped(reporter: MetroAnalyticsReporter): void {
  reporter('my_location_tapped', {})
}

/**
 * `search_entry_tapped { slot⊘ }` — user taps the search / route entry from
 * the map, opening Suggester (fires BEFORE `suggester_open`). `slot` is the
 * route slot being filled (`from` | `to`) when known; omitted otherwise. F2
 * entry attribution (search vs. station-tap path into route building).
 */
export function reportSearchEntryTapped(
  reporter: MetroAnalyticsReporter,
  params: { slot?: string } = {},
): void {
  reporter('search_entry_tapped', { slot: params.slot })
}

/**
 * `deeplink_opened { type, target⊘ }` — app entered via a deep link / widget
 * / shortcut / notification. `target` is the resolved destination (e.g. the
 * station slug / id) when known; omitted otherwise. Entry-point ROI +
 * session attribution.
 */
export function reportDeeplinkOpened(
  reporter: MetroAnalyticsReporter,
  params: { type: DeeplinkType; target?: string },
): void {
  reporter('deeplink_opened', { type: params.type, target: params.target })
}
